import { Button, Input, Pagination, Select } from '@sjuni/ui';
import { Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ListMeta } from '@/lib/api/resource';

/**
 * docs/05-ADMIN-DASHBOARD.md §4.6's controls around the table: search, the
 * per-page selector, the "show deleted" toggle, bulk actions and pagination.
 *
 * Separate from `ResourceTable` because they are separate concerns and one of
 * them is optional per resource, not because they are used apart — every list
 * has both.
 */
export interface ResourceToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  perPage: number;
  onPerPageChange: (value: number) => void;
  showTrashed: boolean;
  onShowTrashedChange: (value: boolean) => void;
  meta?: ListMeta;
  /** `Pagination` is href-based, and §4.6's list state lives in the URL. */
  buildPageHref: (page: number) => string;
  /** Rendered between the search box and the per-page selector. */
  filters?: React.ReactNode;
  /** Shown only while something is selected — §4.6's bulk actions. */
  selectedCount?: number;
  bulkActions?: React.ReactNode;
  /** The screen's primary action, e.g. "New article". */
  primaryAction?: React.ReactNode;
}

const PER_PAGE_OPTIONS = [25, 50, 100];

export function ResourceToolbar({
  search,
  onSearchChange,
  perPage,
  onPerPageChange,
  showTrashed,
  onShowTrashedChange,
  meta,
  buildPageHref,
  filters,
  selectedCount = 0,
  bulkActions,
  primaryAction,
}: ResourceToolbarProps) {
  const { t } = useTranslation();

  /*
   * The input is local and debounced into the query. Typing straight into the
   * URL would refetch on every keystroke; §9's budget aside, the list would
   * flicker through partial-word results the editor never asked for.
   */
  const [typed, setTyped] = useState(search);

  useEffect(() => {
    setTyped(search);
  }, [search]);

  useEffect(() => {
    if (typed === search) {
      return;
    }

    const timer = setTimeout(() => onSearchChange(typed), 300);

    return () => clearTimeout(timer);
  }, [typed, search, onSearchChange]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          type="search"
          label={t('list.search')}
          hideLabel
          placeholder={t('list.searchPlaceholder')}
          icon={<Search size={18} strokeWidth={1.75} />}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          className="min-w-56 flex-1"
        />

        {filters}

        <Select
          label={t('list.perPage')}
          hideLabel
          value={String(perPage)}
          onChange={(event) => onPerPageChange(Number(event.target.value))}
          options={PER_PAGE_OPTIONS.map((value) => ({
            value: String(value),
            label: t('list.perPageOption', { count: value }),
          }))}
        />

        {/*
          §4.6 — the "show deleted" toggle. A button rather than a checkbox
          because it swaps what the list *is* rather than filtering it: the API
          returns only the deleted rows, not both sets.
        */}
        <Button
          type="button"
          size="sm"
          variant={showTrashed ? 'primary' : 'secondary'}
          aria-pressed={showTrashed}
          iconLeft={<Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />}
          onClick={() => onShowTrashedChange(!showTrashed)}
        >
          {t('list.showDeleted')}
        </Button>

        {primaryAction ? <div className="ms-auto">{primaryAction}</div> : null}
      </div>

      {selectedCount > 0 ? (
        <div
          // Announced, because the bar appears as a consequence of selecting
          // and a keyboard user has no reason to look for it.
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-card bg-accent-tint-navy p-3"
        >
          <p className="text-body-sm font-semibold text-primary">
            {t('list.selectedCount', { count: selectedCount })}
          </p>
          {bulkActions}
        </div>
      ) : null}

      {meta && meta.last_page > 1 ? (
        <Pagination
          currentPage={meta.current_page}
          lastPage={meta.last_page}
          buildHref={buildPageHref}
          labels={{
            nav: t('list.pagination'),
            previous: t('list.previous'),
            next: t('list.next'),
            page: (page: number) => t('list.page', { page }),
          }}
        />
      ) : null}
    </div>
  );
}
